/**
 * touch.js — translating a thumb into the keyboard the game already reads.
 *
 * The deliberate choice here is that touch synthesises KEY STATES rather than
 * input frames. A tap on the attack button sets Space in exactly the two maps
 * the keyboard handler writes to, and everything downstream — the local player, the
 * online guest's forwarded frame, the edge-triggered one-shot actions, the
 * mouse-steering release check — carries on with no idea a touchscreen exists.
 *
 * The alternative was a parallel input path merged in alongside the keyboard,
 * and it would have had to be re-merged in four places (readLocalInput, the
 * guest's send, the P2 local branch, noInput) and kept in step with each of
 * them forever. Pretending to be a keyboard is a much smaller surface.
 *
 * The same reasoning as steering.js, one layer lower down: that module resolves
 * a cursor to a boolean frame, this one resolves a thumb to the keys that frame
 * is built from.
 */

/**
 * Is this a touch device that should get on-screen controls?
 *
 * Deliberately not just "does it have a touchscreen" — plenty of laptops do,
 * and covering a quarter of their screen with thumb pads would be absurd. The
 * test is for a device whose PRIMARY pointer is coarse, which is what
 * distinguishes a phone or tablet from a touch-capable desktop.
 *
 * @returns {boolean}
 */
export function isTouchPrimary() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(pointer: coarse)').matches
    && !window.matchMedia('(pointer: fine)').matches;
}

/**
 * Deadzone on the stick, as a fraction of its radius.
 *
 * A thumb resting on a screen is never still, and without this the rider
 * twitches between left and right while the player is holding what feels to
 * them like the centre.
 */
const DEADZONE = 0.28;

/**
 * Vertical threshold, as a fraction of the radius.
 *
 * Higher than the horizontal one on purpose. Pushing a stick "sideways" with a
 * thumb naturally traces an arc, so a symmetrical threshold makes every run
 * also a jump — and on foot, also a guard on the way back down.
 */
const VERTICAL = 0.55;

/** Horizontal threshold, as a fraction of the radius. */
const HORIZONTAL = 0.3;

/**
 * Which movement keys a stick position corresponds to.
 *
 * Returns the same four keys the keyboard scheme uses — the ARROWS — so the
 * caller can write them straight into the held-key map. They moved with the
 * scheme, and had to: w, a, s and d are action keys now, so a stick still
 * writing those would switch board mode every time somebody steered up.
 *
 * @param {{x: number, y: number}} vec Stick offset, each axis in [-1, 1], with
 *   y positive downward to match canvas convention.
 * @returns {{a: boolean, d: boolean, w: boolean, s: boolean}}
 */
export function stickKeys(vec) {
  const magnitude = Math.hypot(vec.x, vec.y);
  if (magnitude < DEADZONE) {
    return {
      arrowleft: false, arrowright: false, arrowup: false, arrowdown: false,
    };
  }
  return {
    arrowleft: vec.x < -HORIZONTAL,
    arrowright: vec.x > HORIZONTAL,
    arrowup: vec.y < -VERTICAL,
    arrowdown: vec.y > VERTICAL,
  };
}

/**
 * Clamp a raw drag offset to the stick's travel, normalised to [-1, 1].
 *
 * @param {number} dx Pixels dragged from the stick centre.
 * @param {number} dy Pixels dragged from the stick centre.
 * @param {number} radius Stick travel in pixels.
 * @returns {{x: number, y: number}} normalised offset, magnitude <= 1.
 */
export function clampStick(dx, dy, radius) {
  const distance = Math.hypot(dx, dy);
  if (distance <= radius) return { x: dx / radius, y: dy / radius };
  // Past the edge the stick pins to the rim rather than running away with the
  // thumb, so sliding off the pad still reads as a clean full-tilt direction.
  const scale = radius / distance;
  return { x: (dx * scale) / radius, y: (dy * scale) / radius };
}

/**
 * The buttons of the thumb console, in reading order.
 *
 * Each names the key it stands in for, and the keyboard's own semantics decide
 * the rest: `hold` keys stay down while the button is touched (the beam charges
 * for as long as you hold it), the rest register as a press per tap.
 *
 * They carry WORDS, and every word is true. The earlier pads were symbols
 * because one pad had to stand for two unrelated moves — Space is a punch on
 * foot and the beam on a board, W a kick on foot and the Feet/Hands switch on
 * a board — and any single word was a lie in one of the two states. Here each
 * meaning gets its own button, and a button is only LIVE in the state its
 * meaning exists in (see `live`): off-state it is dimmed and inert, so ATTACK
 * can never fire the beam and SWAP can never kick. The states are exclusive, so
 * two buttons on one key never compete.
 *
 * Fields:
 *   live(st)  — does this move exist in the current state at all?
 *   ready(st) — how much of it is back, 0..1 (drives the ring; 1 = usable).
 *   hint(st)  — exact wording, for screen readers.
 *   area      — its cell in the console grid (see App.css, .touch-grid).
 */
export const PADS = [
  {
    id: 'guard',
    label: 'Guard',
    tint: '255, 200, 60',
    key: 'shift',
    hold: true,
    area: 'guard',
    icon: () => 'guard',
    // Always live: a hold guards on foot, and a double-tap slings the board
    // from either state, exactly as Shift does on a keyboard.
    live: () => true,
    ready: () => 1,
    hint: ({ onBoard }) => (onBoard
      ? 'Guard — guards on foot; double-tap to sling the board on your back'
      : 'Guard (hold) · with an attack, goes low'),
  },
  {
    id: 'board',
    label: 'Board',
    tint: '64, 160, 255',
    key: 'q',
    hold: false,
    area: 'board',
    // One word covers every one of Q's meanings — kicked from underfoot,
    // thrown from the hands, picked up, recalled — because all of them are
    // "the board".
    icon: () => 'board',
    live: () => true,
    ready: (st) => st.boardReady,
    hint: ({ onBoard, boardMode }) => {
      if (!onBoard) return 'Pick up or recall your board';
      return boardMode === 'hands' ? 'Throw the board' : 'Kick the board away';
    },
  },
  {
    id: 'bomb',
    label: 'Bomb',
    tint: '190, 110, 255',
    key: 'e',
    hold: false,
    area: 'bomb',
    icon: () => 'cluster',
    live: () => true,
    ready: (st) => st.clusterReady,
    hint: ({ clusterAmmo }) => (clusterAmmo > 0
      ? `Throw a cluster bomb (${clusterAmmo} left)`
      : 'Cluster bombs reloading'),
  },
  {
    id: 'attack',
    label: 'Attack',
    tint: '255, 60, 90',
    key: ' ',
    hold: false,
    area: 'attack',
    icon: () => 'fist',
    // Hand to hand exists only with the board gone.
    live: (st) => !st.onBoard,
    ready: () => 1,
    hint: () => 'Punch · with Guard held, a low punch',
  },
  {
    id: 'shot',
    label: 'Shot',
    tint: '40, 200, 255',
    key: ' ',
    hold: true,
    area: 'shot',
    icon: () => 'beam',
    // The beam fires only from a board held in the hands.
    live: (st) => st.onBoard && st.boardMode === 'hands',
    ready: (st) => st.beamReady,
    hint: ({ onBoard }) => (onBoard
      ? 'Fire the beam (hold to charge) — needs Hands mode'
      : 'The beam needs your board'),
  },
  {
    id: 'kick',
    label: 'Kick',
    tint: '60, 230, 120',
    key: 'w',
    hold: false,
    area: 'kick',
    icon: () => 'boot',
    live: (st) => !st.onBoard,
    ready: () => 1,
    hint: () => 'Kick · with Guard held, a sweep',
  },
  {
    id: 'emp',
    label: 'EMP',
    tint: '220, 90, 255',
    key: 'r',
    hold: false,
    area: 'emp',
    icon: () => 'emp',
    live: () => true,
    ready: (st) => st.empReady,
    hint: ({ empAmmo }) => (empAmmo > 0
      ? 'Fire the EMP — a pulse across the whole stage, kills a board for 30s'
      : 'EMP recharging'),
  },
  {
    id: 'swap',
    label: 'Swap',
    tint: '225, 235, 255',
    key: 'w',
    hold: false,
    area: 'swap',
    icon: () => 'swap',
    live: (st) => st.onBoard,
    ready: () => 1,
    hint: () => 'Switch Feet ⇄ Hands',
  },
];

/**
 * What the ON FOOT / BOARD MODE switch should do from the current state, or
 * null if it can do nothing.
 *
 *   'holster' — on the board, going on foot: sling it on your back (the
 *               keyboard's double-tap of Guard).
 *   'unholster' — board on your back: take it off again (the same double-tap).
 *   'fetch'   — board loose in the world: pick it up or recall it (Q).
 *
 * An EMP'd board answers to nothing, so neither way is offered while it is
 * down — the game itself refuses a dead board every other way too.
 *
 * @param {'foot'|'board'} target Which side of the switch was tapped.
 * @param {object} st Pad state (see padSnapshot in App.js).
 * @returns {'holster'|'unholster'|'fetch'|null}
 */
export function modeSwitchAction(target, st) {
  if (st.boardReady < 1) return null;
  if (target === 'foot') return st.onBoard ? 'holster' : null;
  if (st.onBoard) return null;
  return st.holstered ? 'unholster' : 'fetch';
}
